/* @flow */

import { setLoggerGif, setErrorExcludeReporting } from './utils/logger';
import {
  extend,
  isString,
  isArray,
  isFunction,
  pickRandom,
  difference
} from './utils/coreutils';
import {
  PLACEMENT_POSITIONS,
  isValidPlacementPosition
} from './constants/placementPositions';
import { isOptedOut, isFromEU } from './utils/privacyutils';
import { injectGlobalCSS } from './utils/browserutil';

/**
 *
 * @module adconfig
 * @exports adconfig
 * @requires module:logger
 * @requires module:coreutils
 * @description Implements ad configuration functionality
 */

// Tracking-only ad positions that should be excluded when user is opted-out
// Reference: https://docs.prebid.org/dev-docs/modules/userId.html#user-id-gdpr-permissions-and-opt-out
const TRACKING_POSITIONS = new Set(['Frame1']);

/**
 * @global
 * @typedef rotationConfig
 * @property {number} duration Ad rotation duration (in sec)
 * @property {number} durationEnd Duration till rotate ads (in sec)
 * @property {string} positions Ad positions to rotate
 * @property {string} exclude Exclude browser & os combination from rotation
 * @property {boolean} onlyActivePage Rotate only when the page is active(default:off)
 */

class _adconfig {
  _win: WindowProxy;

  _doc: Document;

  _pageInfo: pageInfo;

  _lastConfigLoadTime: number;

  _gPageInfo: pageInfo;

  _defConfig: adConfig;

  _config: adConfig;

  _configOverride: {};

  _lastGlobalCSS: string;

  constructor() {
    this._win = window;
    this._doc = document;
    /**
     * @global
     * @external hbConfig
     * @typedef adConfig
     * @property {object} settings HB Settings
     * @property {rotationConfig} settings.adRotate Ad rotation config
     * @property {object.<string, hbConfig>} settings.headerBidding HB config map
     * @property {object.<string, posConfigInfo>} settings.posMap Position map
     * @property {object.<string, module:_adserverbase._perAdServerConfig>} settings.adServer Ad server map
     * @property {string} settings.loggerGif Logger path(gif image)
     * @property {number} settings.configExpirtySec Config cache duration(in sec)
     * @property {number} settings.loadingDelay Delay
     */
    this._lastGlobalCSS = '';
    this._defConfig = {
      settings: {
        cookieManager: {
          checkInterval: 0,
          reportingLength: 4000,
          cleanupLength: 5000,
          forceCleanupLength: 0,
          dndCookies: [],
          cleanupCookies: [],
          minCleanupCookieLength: 100
        },
        adRotate: {
          exclude: '',
          positions: '',
          delay: 0,
          duration: 60,
          durationEnd: -1,
          onlyActivePage: false
        },
        headerBidding: {
          bidders: [],
          bidders_dialup: [],
          biddersOverride: [],
          shuffleBidders: false,
          timeout: 3000,
          userSync: {},
          granularity: 'dense',
          schain: {}
        },
        loggerGif: '//static.uolcontent.com/images/spacer.gif',
        errExcludeReporting: '',
        configExpirySec: 20 * 60,
        creatives: {},
        posMap: {},
        adServer: {},
        il: {
          obj: 'I12C',
          key: 'Morph'
        },
        adloading: {
          delay: 200,
          sandbox: ''
        },
        userIds: {
          ids: []
        },
        cvFilter: [],
        isGPPSupported: true,
        disableAdsForEU: false,
        globalCSS: '',
        excludePositions: []
      }
    };
    this._config = extend(true, {}, this._defConfig);
    this._pageInfo = {
      posList: [],
      name: ''
    };
    this._lastConfigLoadTime = 0;
    this._gPageInfo = {
      posList: [],
      name: ''
    };
    this._configOverride = {};
  }

  /**
   * Initialize the object
   *
   * @param {window} w - window object
   * @param {Document} d - document object
   * @param {module:globalParams.globalParams} g - global param object
   * @returns {undefined}
   */
  init(w: WindowProxy, d: Document, g: globalParam) {
    this._win = w;
    this._doc = d;
    this._configOverride = g.adConfig.override || {};
    this._gPageInfo = g.pageInfo;
    setLoggerGif(this._config.settings.loggerGif);
    setErrorExcludeReporting(this._config.settings.errExcludeReporting);
  }

  /**
   * Sets pageName & posList. posList is always cached as Array.
   *
   * @param {string} [pageName] - Pagename
   * @param {string|Array<string>} [posList] - Position list
   * @returns {undefined}
   */
  setPageInfo(pageName: ?string = '', posList: ?(string | Array<string>) = '') {
    const list = posList || '';
    this._pageInfo.name = pageName || '';
    if (isString(list) && list.length) {
      // $FlowFixMe
      this._pageInfo.posList = list.split(',');
    } else if (isArray(list) && list.length) {
      this._pageInfo.posList = list;
    }
  }

  /**
   * @global
   * @callback getPageNameCB
   * @param {object} context
   */
  /**
   * Getter function for pagename
   *
   * @param {string|getPageNameCB} s - input can be string or function
   * @returns {string} - Page name
   */
  getPageName(s: ?(string | (WindowProxy => string)) = ''): string {
    // flowlint-next-line sketchy-null-string:off
    const pageName = s || this._pageInfo.name || this._gPageInfo.name;

    if (isString(pageName)) {
      // $FlowFixMe
      return pageName;
    }
    if (isFunction(pageName)) {
      // $FlowFixMe
      return pageName.call(this._win);
    }
    return '';
  }

  /**
   * @global
   * @callback getPagePositionsCB
   * @param {object} context
   * @param {string} page - pagename
   */
  /**
   * Getter function for ad positions for the current page
   *
   * @param {string|Array<string>|getPagePositionsCB} [v] - list of ad positions
   * @param {string|getPageNameCB} pageName - current page name
   * @param {boolean} [bRotation=false] - whether called during ad rotation
   * @returns {Array<string>} - Ad positions
   */
  getPagePositions(
    v: ?(string | Array<string> | ((WindowProxy, string) => string)),
    pageName: string | (WindowProxy => string) = '',
    bRotation: boolean = false
  ): Array<string> {
    let posList = v;
    // flowlint-next-line sketchy-null-string:off
    if (!posList && this._pageInfo.posList.length > 0) {
      // eslint-disable-next-line prefer-destructuring
      posList = this._pageInfo.posList;
    }

    // flowlint-next-line sketchy-null-string:off
    if (!posList) {
      // eslint-disable-next-line prefer-destructuring
      posList = this._gPageInfo.posList;
    }

    if (isFunction(posList)) {
      // $FlowFixMe
      posList = posList.call(this._win, this.getPageName(pageName), bRotation);
    }
    posList = posList || '';
    // get and return array
    if (isString(posList) && posList.length) {
      // $FlowFixMe
      posList = posList.split(',');
    } else if (!isArray(posList)) {
      posList = [];
    }
    // Filter tracking positions if user is opted-out
    if (isOptedOut() && isArray(posList) && posList.length > 0) {
      // $FlowFixMe
      posList = posList.filter(pos => !TRACKING_POSITIONS.has(pos));
    }
    // Filter excluded positions from config using difference helper
    const excludePositions = this._config.settings.excludePositions || [];
    if (
      isArray(excludePositions) &&
      excludePositions.length > 0 &&
      isArray(posList) &&
      posList.length > 0
    ) {
      // $FlowFixMe - posList is guaranteed to be Array<string> at this point
      posList = difference(posList, excludePositions);
    }
    // $FlowFixMe
    return posList;
  }

  /**
   * processConfigData - take cares of updating caches and propagating config change
   *
   * @param {adConfig} data - AdConfig data
   * @returns {undefined}
   * @access private
   */

  processConfigData(data: adConfig) {
    // update in-memory cache
    this._config = extend(
      true,
      {},
      this._defConfig,
      data,
      this._configOverride
    );

    if (this._config.settings.loggerGif) {
      setLoggerGif(this._config.settings.loggerGif);
    }
    setErrorExcludeReporting(this._config.settings.errExcludeReporting);

    const globalCSS =
      (typeof this._config.settings.globalCSS === 'string'
        ? this._config.settings.globalCSS
        : '');
    if (globalCSS !== this._lastGlobalCSS) {
      injectGlobalCSS(this._doc, 'asjs-global-css', globalCSS);
      this._lastGlobalCSS = globalCSS;
    }
  }

  /**
   * getter for config
   *
   * @returns {adConfig} - Ad Config
   */
  get(): adConfig {
    return this._config;
  }

  /**
   * Getter for global SupplyChain config
   * @returns {anyObject} - schain object or empty object
   */
  getSchain(): anyObject {
    const { headerBidding: { schain = {} } = {} } = this._config.settings || {};
    return schain;
  }

  /**
   * getter for creatives
   *
   * @param {string} pos - Ad Position
   * @returns {string} - Creatives string
   */
  getCreative(pos: string = ''): string {
    let ret = '';
    if (!pos.length) {
      return ret;
    }
    const posInfo = this.getPosSettings(pos);
    const creativeKey = posInfo.creative || '';
    if (isString(creativeKey) && creativeKey.length === 0) {
      // not a valid creative key configured here
      return ret;
    }

    const creativeKeys: Array<string> = isString(creativeKey)
      ? // $FlowFixMe
        [creativeKey]
      : // $FlowFixMe
        creativeKey;
    const creativeLen = creativeKeys.length;
    if (creativeLen === 0) {
      return ret;
    }

    const creatives = this._config.settings.creatives || {};
    ret = creatives[pickRandom(creativeKeys)] || '';
    return ret;
  }

  /**
   * @global
   * @callback contentHandlerCB
   * @param {string} page - pagename
   * @param {string} pos - ad position
   * @param {string} data - ad content
   */

  /**
   * @global
   * @typedef posConfigInfo
   * @property {string} as Ad server name
   * @property {boolean} [aFold=false] Above Fold/not
   * @property {string} [auid] AdUnit ID
   * @property {number} width Width
   * @property {number} height Height
   * @property {boolean} [iframe=true] Should be loaded in iframe?
   * @property {boolean} [gscript=false] Should execute the creative on top window. Support CSS/JS injection. (Deprecated)
   * @property {boolean} [dynHeight=false] Dynamically expand height (Deprecated)
   * @property {string} [outOfPageAdType] Out of Page Ad Type
   * @property {number}	[MarginHeight] Margin Height (Deprecated)
   * @property {contentHandlerCB}	callback Callback after fetching ad(deprecated)
   */
  /**
   * getter for position specific config
   *
   * @param {string} s - position name
   * @returns {posConfigInfo} - Position config
   */
  getPosSettings(s: string): posConfigInfo {
    // by default we will return 1*1 adSettings
    const _def: posConfigInfo = {
      width: 1,
      height: 1,
      as: '',
      loadingMethod: 1,
      minPercentRotationViewport: 0,
      onlyActivePage: false,
      adUnitPath: '',
      outOfPageAdType: '',
      aFold: false,
      sizes: [],
      placementPosition: PLACEMENT_POSITIONS.UNKNOWN
    };
    let ret: posConfigInfo = {};
    if (s.length > 0 && this._config.settings.posMap[s]) {
      ret = this._config.settings.posMap[s];
    }
    ret = extend(true, _def, ret);
    return ret;
  }

  /**
   * getter for above the fold or not
   *
   * @param {string} pos - Ad Position
   * @returns {boolean} - is above fold?
   */
  isAboveFold(pos: string = ''): boolean {
    // By default all positions are below fold
    let aFold: boolean = false;
    if (!pos.length) {
      return aFold;
    }
    aFold = Boolean(this.getPosSettings(pos).aFold);
    return aFold;
  }

  /**
   * Check for out of page ad type
   * @param {string} pos - Ad position
   * @returns {boolean} - Is Out of page ad type OR not
   */
  isOutOfPageAd(pos: string = ''): boolean {
    if (pos.length === 0) {
      return false;
    }
    const adType = this.getPosSettings(pos).outOfPageAdType;
    return [
      'LEFT_SIDE_RAIL',
      'RIGHT_SIDE_RAIL',
      'TOP_ANCHOR',
      'BOTTOM_ANCHOR'
    ].includes(adType);
  }

  /**
   * getter for out of page ad type
   * @param {string} pos - Ad position
   * @returns {string} - Out of page value
   */
  getOutOfPageAdType(pos: string = ''): string {
    if (!this.isOutOfPageAd(pos)) {
      return '';
    }
    const { outOfPageAdType = '' } = this.getPosSettings(pos);
    return outOfPageAdType;
  }

  /**
   * Getter for GPID value
   * Uses GAM adUnitPath if available
   * @param {string} pos - Ad position
   * @returns {String} - generated GPID value
   */
  getGPID(pos: string = ''): string {
    let ret = '';
    if (pos.length <= 0) {
      return ret;
    }
    const { adUnitPath = '' } = this.getPosSettings(pos);
    ret = adUnitPath.length > 0 ? `${adUnitPath}#${pos}` : pos;
    return ret;
  }

  /**
   * Getter for placement position
   * Always returns a valid placement position code per AdCOM 1.0
   * @param {string} pos - Ad position
   * @returns {number} - valid placement position code (0-7)
   */
  getPlacementPosition(pos: string = ''): number {
    let ret = PLACEMENT_POSITIONS.UNKNOWN;
    if (pos.length <= 0) {
      return ret;
    }
    const { placementPosition = PLACEMENT_POSITIONS.UNKNOWN, aFold = false } =
      this.getPosSettings(pos);

    if (isValidPlacementPosition(placementPosition)) {
      ret = placementPosition;
    }

    // If placement position is UNKNOWN but we have aFold info, use it for better targeting
    if (ret === PLACEMENT_POSITIONS.UNKNOWN) {
      ret = aFold
        ? PLACEMENT_POSITIONS.ABOVE_THE_FOLD
        : PLACEMENT_POSITIONS.BELOW_THE_FOLD;
    }

    return ret;
  }

  /**
   * Check if ads should be disabled for EU users
   * Centralized helper that checks both config flag and EU detection
   * @returns {boolean} - True if ads should be disabled for EU
   */
  isAdsDisabledForEU(): boolean {
    return Boolean(this._config.settings.disableAdsForEU) && isFromEU();
  }
}

// export singleton object as default for this module
const ac: _adconfig = new _adconfig();
export default ac;
